import * as C from "../constants/chatConstants";
import api from "../utils/axiosInstance";
import { getApiErrorMessage } from "../utils/errorMessages";

const getErrorMessage = (error, fallback) =>
  getApiErrorMessage(error, { fallback });

const normalizeString = (value = "") => String(value || "").trim();

const encodePath = (value = "") => encodeURIComponent(normalizeString(value));

function buildClientError(message, code = "BAD_REQUEST", status = 400) {
  const error = new Error(message);
  error.code = code;
  error.status = status;
  return error;
}

function normalizeStartPayload(payload = {}) {
  return {
    name: normalizeString(payload.name),
    firstName: normalizeString(payload.firstName),
    email: normalizeString(payload.email),
    message: normalizeString(payload.message),
    enquiryType: normalizeString(payload.enquiryType),
    product: payload.product || {},
  };
}

function normalizeIdentityPayload(payload = {}) {
  return {
    firstName: normalizeString(payload.firstName),
    email: normalizeString(payload.email),
    product: payload.product || {},
    website: normalizeString(payload.website),
    company: normalizeString(payload.company),
  };
}

function normalizeMessagePayload(message = "") {
  const safeMessage = normalizeString(message);

  if (!safeMessage) {
    throw buildClientError("Message is required.", "CHAT_MESSAGE_REQUIRED");
  }

  return {
    message: safeMessage,
  };
}

export const getMyChatConversation = () => async (dispatch) => {
  try {
    dispatch({ type: C.CUSTOMER_CHAT_CURRENT_REQUEST });

    const { data } = await api.get("/chat/conversations/current");

    dispatch({
      type: C.CUSTOMER_CHAT_CURRENT_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load your chat right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_CURRENT_FAIL,
      payload,
    });

    throw error;
  }
};

export const getMyChatConversationById = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({ type: C.CUSTOMER_CHAT_CURRENT_REQUEST, meta: { id: safeId } });

    if (!safeId) {
      throw buildClientError(
        "Conversation id is required.",
        "CHAT_CONVERSATION_REQUIRED",
      );
    }

    const { data } = await api.get(`/chat/conversations/${encodePath(safeId)}`);

    dispatch({
      type: C.CUSTOMER_CHAT_CURRENT_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load this chat right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_CURRENT_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const startChatConversation = (payload = {}) => async (dispatch) => {
  try {
    dispatch({ type: C.CUSTOMER_CHAT_START_REQUEST });

    const { data } = await api.post(
      "/chat/conversations",
      normalizeStartPayload(payload),
    );

    dispatch({
      type: C.CUSTOMER_CHAT_START_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not start chat right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_START_FAIL,
      payload,
    });

    throw error;
  }
};

export const identifyChatGuest = (payload = {}) => async (dispatch) => {
  try {
    dispatch({ type: C.CUSTOMER_CHAT_IDENTITY_REQUEST });

    const { data } = await api.post(
      "/chat/identity",
      normalizeIdentityPayload(payload),
    );

    dispatch({
      type: C.CUSTOMER_CHAT_IDENTITY_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not send your verification email right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_IDENTITY_FAIL,
      payload,
    });

    throw error;
  }
};

export const resendChatVerification = () => async (dispatch) => {
  try {
    dispatch({ type: C.CUSTOMER_CHAT_RESEND_REQUEST });

    const { data } = await api.post("/chat/identity/resend", {});

    dispatch({
      type: C.CUSTOMER_CHAT_RESEND_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not resend your verification email right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_RESEND_FAIL,
      payload,
    });

    throw error;
  }
};

export const clearChatGuestIdentity = () => async (dispatch) => {
  try {
    dispatch({ type: C.CUSTOMER_CHAT_CLEAR_IDENTITY_REQUEST });

    const { data } = await api.post("/chat/identity/clear", {});

    dispatch({
      type: C.CUSTOMER_CHAT_CLEAR_IDENTITY_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not change your chat details right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_CLEAR_IDENTITY_FAIL,
      payload,
    });

    throw error;
  }
};

export const sendMyChatMessage = (id, message) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({ type: C.CUSTOMER_CHAT_SEND_REQUEST, meta: { id: safeId } });

    if (!safeId) {
      throw buildClientError(
        "Conversation id is required.",
        "CHAT_CONVERSATION_REQUIRED",
      );
    }

    const { data } = await api.post(
      `/chat/conversations/${encodePath(safeId)}/messages`,
      normalizeMessagePayload(message),
    );

    dispatch({
      type: C.CUSTOMER_CHAT_SEND_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not send your chat message right now. Please try again later.",
    );

    dispatch({
      type: C.CUSTOMER_CHAT_SEND_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const markMyChatRead = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) return null;

  const { data } = await api.post(
    `/chat/conversations/${encodePath(safeId)}/read`,
    {},
  );

  dispatch({
    type: C.CUSTOMER_CHAT_READ_SUCCESS,
    payload: data,
    meta: { id: safeId },
  });

  return data;
};

export const receiveCustomerChatMessage = (payload) => ({
  type: C.CUSTOMER_CHAT_RECEIVE_MESSAGE,
  payload,
});

export const updateCustomerChatConversation = (payload) => ({
  type: C.CUSTOMER_CHAT_CONVERSATION_UPDATE,
  payload,
});

export const resetCustomerChat = () => ({ type: C.CUSTOMER_CHAT_RESET });

export const listChatConversationsAdmin =
  ({ status = "open", search = "", limit = 100 } = {}) =>
  async (dispatch) => {
    try {
      dispatch({
        type: C.ADMIN_CHAT_LIST_REQUEST,
        meta: { status, search, limit },
      });

      const { data } = await api.get("/chat/admin/conversations", {
        params: {
          status: normalizeString(status) || "open",
          search: normalizeString(search),
          limit,
        },
      });

      dispatch({
        type: C.ADMIN_CHAT_LIST_SUCCESS,
        payload: data,
        meta: { status, search, limit },
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not load chat conversations right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_CHAT_LIST_FAIL,
        payload,
        meta: { status, search, limit },
      });

      throw error;
    }
  };

export const getChatConversationAdmin = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({ type: C.ADMIN_CHAT_DETAILS_REQUEST, meta: { id: safeId } });

    if (!safeId) {
      throw buildClientError(
        "Conversation id is required.",
        "CHAT_CONVERSATION_REQUIRED",
      );
    }

    const { data } = await api.get(
      `/chat/admin/conversations/${encodePath(safeId)}`,
    );

    dispatch({
      type: C.ADMIN_CHAT_DETAILS_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load this conversation right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_CHAT_DETAILS_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const sendChatMessageAdmin = (id, message) => async (dispatch) => {
  const safeId = normalizeString(id);

  try {
    dispatch({ type: C.ADMIN_CHAT_SEND_REQUEST, meta: { id: safeId } });

    if (!safeId) {
      throw buildClientError(
        "Conversation id is required.",
        "CHAT_CONVERSATION_REQUIRED",
      );
    }

    const { data } = await api.post(
      `/chat/admin/conversations/${encodePath(safeId)}/messages`,
      normalizeMessagePayload(message),
    );

    dispatch({
      type: C.ADMIN_CHAT_SEND_SUCCESS,
      payload: data,
      meta: { id: safeId },
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not send this chat reply right now. Please try again later.",
    );

    dispatch({
      type: C.ADMIN_CHAT_SEND_FAIL,
      payload,
      meta: { id: safeId },
    });

    throw error;
  }
};

export const markChatConversationReadAdmin = (id) => async (dispatch) => {
  const safeId = normalizeString(id);

  if (!safeId) return null;

  const { data } = await api.post(
    `/chat/admin/conversations/${encodePath(safeId)}/read`,
    {},
  );

  dispatch({
    type: C.ADMIN_CHAT_READ_SUCCESS,
    payload: data,
    meta: { id: safeId },
  });

  return data;
};

export const updateChatConversationStatusAdmin =
  (id, status) => async (dispatch) => {
    const safeId = normalizeString(id);
    const safeStatus = normalizeString(status).toLowerCase();

    try {
      dispatch({
        type: C.ADMIN_CHAT_STATUS_REQUEST,
        meta: { id: safeId, status: safeStatus },
      });

      if (!safeId) {
        throw buildClientError(
          "Conversation id is required.",
          "CHAT_CONVERSATION_REQUIRED",
        );
      }

      const { data } = await api.patch(
        `/chat/admin/conversations/${encodePath(safeId)}/status`,
        { status: safeStatus },
      );

      dispatch({
        type: C.ADMIN_CHAT_STATUS_SUCCESS,
        payload: data,
        meta: { id: safeId, status: safeStatus },
      });

      return data;
    } catch (error) {
      const payload = getErrorMessage(
        error,
        "We could not update this conversation right now. Please try again later.",
      );

      dispatch({
        type: C.ADMIN_CHAT_STATUS_FAIL,
        payload,
        meta: { id: safeId, status: safeStatus },
      });

      throw error;
    }
  };

export const getChatUnreadAdmin = () => async (dispatch) => {
  try {
    dispatch({ type: C.ADMIN_CHAT_UNREAD_REQUEST });

    const { data } = await api.get("/chat/admin/unread");

    dispatch({
      type: C.ADMIN_CHAT_UNREAD_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = getErrorMessage(
      error,
      "We could not load chat unread counts right now.",
    );

    dispatch({
      type: C.ADMIN_CHAT_UNREAD_FAIL,
      payload,
    });

    throw error;
  }
};

export const receiveAdminChatMessage = (payload) => ({
  type: C.ADMIN_CHAT_RECEIVE_MESSAGE,
  payload,
});

export const updateAdminChatConversation = (payload) => ({
  type: C.ADMIN_CHAT_CONVERSATION_UPDATE,
  payload,
});

export const resetActiveAdminChat = () => ({ type: C.ADMIN_CHAT_RESET_ACTIVE });
